import React, { useState, useEffect, useCallback, useContext } from "react";
import { auth, db } from "../firebase";
import { doc, getDoc } from "firebase/firestore";
import './MyCompany.css';
import Tooltip from "./Tooltip";
import ConfirmModal from "./ConfirmModal";
import { NotificationsContext } from "../hooks/notificationsContext";

export default function CompanyIssueTab({ companyDoc }) {
  const [isBuyback, setIsBuyback] = useState(false);
  const [companyStockVolume, setCompanyStockVolume] = useState(0);
  const [companyOutstanding, setCompanyOutstanding] = useState(0);
  const [companyCrypto, setCompanyCrypto] = useState(false);
  const [quantity, setQuantity] = useState("");
  const [price, setPrice] = useState("");
  const [cryptoChoice, setCryptoChoice] = useState(false); 
  const [loading, setLoading] = useState(false);
  const [fees, setFees] = useState(null);
  const [buybackShareCost, setBuybackShareCost] = useState(0);
  const [showConfirm, setShowConfirm] = useState(false);
  const [globalTaxRate, setGlobalTaxRate] = useState(0);

  const { addNotification } = useContext(NotificationsContext);

  useEffect(() => {
    async function fetchGlobalVars() {
      try {
        const globalDocRef = doc(db, "Global_Vars", "Main");
        const globalDocSnap = await getDoc(globalDocRef);
        if (globalDocSnap.exists()) {
          setGlobalTaxRate(globalDocSnap.data().globalBaseTaxRate / 100);
        }
      } catch (err) { console.error(err); }
    }
    fetchGlobalVars();
  }, []);

  useEffect(() => {
    if (companyDoc) {
      setCompanyStockVolume(companyDoc.companyStockVolume || 0);
      setCompanyOutstanding(companyDoc.companyOutstanding || 0);
      setCompanyCrypto(companyDoc.companyCrypto || false);
      if (companyDoc.companyStockPrice) setPrice(companyDoc.companyStockPrice);
    }
  }, [companyDoc]);

  const calculateFeesAndCosts = useCallback(() => {
    const qty = Number(quantity);
    const prc = Number(price);
    if (!qty || qty <= 0 || (!isBuyback && qty < 100) || !prc || globalTaxRate === 0) {
      setFees(null);
      setBuybackShareCost(0);
      return;
    }

    const region = companyDoc.companyRegion || "NONE";
    const currentPrice = companyDoc.companyStockPrice || prc;

    if (isBuyback) {
      let feeMultiplier = 1.0;
      if (region === "NA") {
        feeMultiplier = companyCrypto ? 1.0 : 1.5;
      } else {
        if (companyCrypto) feeMultiplier = 1.1;
        else feeMultiplier = 1.65;
      }
      
      const totalFee = (currentPrice * qty) * globalTaxRate * feeMultiplier;
      setFees(Math.round(totalFee * 100) / 100);

      if (qty > companyOutstanding) {
        const ownedSharesToBuy = qty - companyOutstanding;
        const totalShareCost = ownedSharesToBuy * currentPrice;
        setBuybackShareCost(Math.round(totalShareCost * 100) / 100);
      } else {
        setBuybackShareCost(0);
      }
      return;
    }

    const alreadyIssued = companyStockVolume > 0;
    const finalPrice = alreadyIssued ? currentPrice : prc;
    const finalCrypto = alreadyIssued ? companyCrypto : cryptoChoice;
    const base = finalPrice * qty;

    let feeMultiplier = 1.0;
    if (region === "NA") {
      feeMultiplier = alreadyIssued ? (finalCrypto ? 1.0 : 1.5) : 1.0;
    } else {
      if (!alreadyIssued && !finalCrypto) feeMultiplier = 1.5;
      else if (alreadyIssued && finalCrypto) feeMultiplier = 1.1;
      else if (alreadyIssued && !finalCrypto) feeMultiplier = 1.65;
    }

    let calcFees = base * globalTaxRate * feeMultiplier;
    if (region === "NA" && !alreadyIssued) calcFees *= 0.5;
    
    setFees(Math.round(calcFees * 100) / 100);
    setBuybackShareCost(0);
  }, [quantity, price, cryptoChoice, companyStockVolume, companyCrypto, companyOutstanding, companyDoc, globalTaxRate, isBuyback]);

  useEffect(() => { calculateFeesAndCosts(); }, [quantity, price, cryptoChoice, isBuyback, calculateFeesAndCosts]);

  const handleExecuteAction = async () => {
    setShowConfirm(false);
    setLoading(true);

    const endpoint = isBuyback 
      ? "https://buybackstock-5dhfwyjvzq-uc.a.run.app" 
      : "https://issuestock-5dhfwyjvzq-uc.a.run.app";

    try {
      const token = await auth.currentUser.getIdToken(true);
      const res = await fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          Quantity: Number(quantity),
          Crypto: companyStockVolume > 0 ? companyCrypto : cryptoChoice,
          Price: companyStockVolume > 0 ? undefined : Number(price),
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `Failed to ${isBuyback ? 'buy back' : 'issue'}`);
      
      const successMessage = isBuyback 
        ? `Successfully bought back shares. Fees: $${data.feesCharged.toFixed(2)}. Share payouts: $${buybackShareCost.toFixed(2)}`
        : `Successfully issued. Fees: $${data.feesCharged.toFixed(2)}`;

      addNotification(successMessage, "success");
      setQuantity("");
    } catch (err) {
      addNotification(err.message, "error");
    } finally { setLoading(false); }
  };

  const isCrypto = companyStockVolume > 0 ? companyCrypto : cryptoChoice;
  const assetType = isCrypto ? "COIN" : "SHARE";
  const totalTransactionCost = (fees || 0) + buybackShareCost;

  const qtyNum = Number(quantity);
  const isButtonDisabled = !qtyNum || qtyNum <= 0 || (!isBuyback && qtyNum < 100);
  const tooltipText = !qtyNum || qtyNum <= 0 ? "Enter a valid quantity" : "Minimum 100 units required";

  return (
    <div className="issue-tab-container">
      {/* Operation Toggle Switch */}
      <div style={{ display: "flex", gap: "10px", marginBottom: "20px" }}>
        <button
          onClick={() => { setIsBuyback(false); setQuantity(""); }}
          style={{
            flex: 1, padding: "12px", borderRadius: "4px", cursor: "pointer", fontFamily: "inherit", fontWeight: "bold",
            background: !isBuyback ? "#50E3C2" : "rgba(255,255,255,0.05)",
            color: !isBuyback ? "#111" : "#888",
            border: "1px solid " + (!isBuyback ? "#50E3C2" : "rgba(255,255,255,0.1)")
          }}
        >
          ISSUE
        </button>
        <button
          onClick={() => { setIsBuyback(true); setQuantity(""); }}
          style={{
            flex: 1, padding: "12px", borderRadius: "4px", cursor: "pointer", fontFamily: "inherit", fontWeight: "bold",
            background: isBuyback ? "#FF5A5F" : "rgba(255,255,255,0.05)",
            color: isBuyback ? "#FFF" : "#888",
            border: "1px solid " + (isBuyback ? "#FF5A5F" : "rgba(255,255,255,0.1)")
          }}
        >
          BUY BACK
        </button>
      </div>

      {/* Header Info Cards */}
      <div className="issue-stats-grid">
        <div className="stat-card">
          <label>Total Issued</label>
          <div className="stat-value">
            {companyStockVolume.toLocaleString()}
          </div>
        </div>
        <div className="stat-card highlight">
          <label>Asset Class</label>
          <div className="stat-value">{assetType}</div>
        </div>
      </div>

      <div className="issue-main-content">
        <div className="input-section">
          {/* ASSET CLASS SELECTION */}
          {companyStockVolume === 0 && !isBuyback && (
            <div className="asset-choice-container" style={{ marginBottom: "20px" }}>
              <label style={{ fontSize: "0.7rem", color: "#888", display: "block", marginBottom: "8px" }}>SELECT ASSET CLASS</label>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px" }}>
                <button 
                  onClick={() => setCryptoChoice(false)}
                  style={{
                    padding: "10px", fontFamily: "inherit", fontSize: "0.8rem", cursor: "pointer",
                    background: !cryptoChoice ? "rgba(80, 227, 194, 0.2)" : "rgba(0,0,0,0.2)",
                    border: "1px solid " + (!cryptoChoice ? "#50E3C2" : "rgba(255,255,255,0.1)"),
                    color: !cryptoChoice ? "#50E3C2" : "#666"
                  }}
                >
                  STANDARD (SHARE)
                </button>
                <button 
                  onClick={() => setCryptoChoice(true)}
                  style={{
                    padding: "10px", fontFamily: "inherit", fontSize: "0.8rem", cursor: "pointer",
                    background: cryptoChoice ? "rgba(80, 227, 194, 0.2)" : "rgba(0,0,0,0.2)",
                    border: "1px solid " + (cryptoChoice ? "#50E3C2" : "rgba(255,255,255,0.1)"),
                    color: cryptoChoice ? "#50E3C2" : "#666"
                  }}
                >
                  CRYPTOCURRENCY (COIN)
                </button>
              </div>
            </div>
          )}

          <div className="cool-input-group">
            <label>{isBuyback ? "Buyback Quantity" : "Issue Quantity"}</label>
            <input 
              type="number" 
              placeholder={isBuyback ? "Enter amount" : "100 minimum"} 
              value={quantity} 
              onChange={(e) => setQuantity(e.target.value)} 
            />
            <span className="input-unit">units</span>
          </div>

          {companyStockVolume === 0 && !isBuyback && (
            <div className="cool-input-group">
              <label>Initial Listing Price</label>
              <input 
                type="number" 
                value={price} 
                onChange={(e) => setPrice(e.target.value)} 
              />
              <span className="input-unit">USD</span>
            </div>
          )}
        </div>

        <div className="issue-preview-card" style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
          <div className="preview-row" style={{ display: "flex", justifyContent: "space-between", fontSize: "0.9rem" }}>
            <span>TAX:</span>
            <span>${fees ? fees.toLocaleString(undefined, {minimumFractionDigits: 2}) : "0.00"}</span>
          </div>
          
          {isBuyback && (
            <div className="preview-row" style={{ display: "flex", justifyContent: "space-between", fontSize: "0.9rem", borderBottom: "1px solid rgba(255,255,255,0.1)", paddingBottom: "8px" }}>
              <span>SHAREHOLDER PAYOUTS:</span>
              <span>${buybackShareCost.toLocaleString(undefined, {minimumFractionDigits: 2})}</span>
            </div>
          )}

          <div className="preview-row total" style={{ display: "flex", justifyContent: "space-between", fontWeight: "bold" }}>
            <span>TOTAL COST:</span>
            <span>${totalTransactionCost.toLocaleString(undefined, {minimumFractionDigits: 2})}</span>
          </div>
        </div>
      </div>

      <div className="action-area">
        {isButtonDisabled ? (
          <Tooltip text={tooltipText}>
            <button className="issue-btn-disabled" disabled>
              {isBuyback ? `Buyback ${assetType}s` : `Issue ${assetType}s`}
            </button>
          </Tooltip>
        ) : (
          <button 
            className={`issue-btn-active ${loading ? 'loading' : ''}`} 
            style={isBuyback ? { backgroundColor: "#FF5A5F", borderColor: "#FF5A5F" } : {}}
            onClick={() => setShowConfirm(true)}
            disabled={loading}
          >
            {loading ? "Processing..." : isBuyback ? `Buyback ${assetType}s` : `Issue ${assetType}s`}
          </button>
        )}
      </div>

      {showConfirm && (
        <ConfirmModal
          title={isBuyback ? "Confirm Buyback Authorization" : "Authorization"}
          message={
            isBuyback 
              ? `Confirming the buyback of ${quantity} ${assetType}s at current valuation. The final cost is $${totalTransactionCost.toFixed(2)} ($${fees.toFixed(2)} fees + $${buybackShareCost.toFixed(2)} to shareholders). This action is irreversible.`
              : `Confirming the issuance of ${quantity} ${assetType}s at $${Number(price).toFixed(2)}. This action is irreversible.`
          }
          confirmText="Confirm"
          cancelText="Cancel"
          onConfirm={handleExecuteAction}
          onCancel={() => setShowConfirm(false)}
        />
      )}
    </div>
  );
}